import { https } from "../http";
import store from "../store";
import { reportSkaiSpend, reportSkaiRejection } from "../utils/skaiAnalytics";
import {
  setAddNoteLoading,
  setAllNotesTypes,
  setBillingNotes,
  setFieldTypes,
  setLoading,
  setLoadingNote_Report,
  setLoadingNote_Types,
  setNotePageLoaging,
  setNoteTypes,
  setNote_Report,
  setNotes,
  setNotesAgainstType,
  setSingleNote,
  setDrafts,
  setDraftsLoading,
  bumpDraftSaved,
} from "../store/reducers/note";
import { showError } from "../utils/index";
import swal from "sweetalert";
import {  getPlans } from "./supportplan";
import { setDashboardNotes } from "../store/reducers/dashboard";
import { getTodaysNotes } from "./dashboard";
import { getSkaiTokenUsage } from "./skaiTokens";

// GET
const getFieldTypes = async (showLoading = true) => {
  try {
    let response = await https().get("/field-type");

    if (response?.status === 200) {
      store.dispatch(setFieldTypes(response?.data));
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};
const getNoteTypes = async (page, showLoading = true) => {
  try {
    if (showLoading) store.dispatch(setLoading(true));
    let response = await https().get(`/ntype/${page}`);

    if (response?.status === 200) {
      store.dispatch(setLoading(false));
      store.dispatch(setNoteTypes(response?.data));
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const getAllNoteTypes = async (showLoading = true) => {
  try {
    if (showLoading) store.dispatch(setLoading(true));
    let response = await https().get(`/ntype/all`);

    if (response?.status === 200) {
      store.dispatch(setLoading(false));
      store.dispatch(setAllNotesTypes(response?.data));
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// Monotonic request counter: rapid filter/tab/page changes fire several
// overlapping /note requests, and the notes list + status/KPI counts both come
// from the same response. Without ordering, a slow stale response can land
// after a fresh one and overwrite it, making rows and counts flap. Only the
// most-recently-started request is allowed to update the store.
let notesRequestSeq = 0;
const getNotes = async (
  page,
  userId,
  showLoading = true,
  dateRange,
  sortField = "createdAt",
  sortOrder = "descending",
  searchStr,
  filters = {}
) => {
  const reqId = ++notesRequestSeq;
  try {
    if (showLoading) store.dispatch(setNotePageLoaging(true));
    ///mar/get?client=${userId}&page=${page}
    let clientParam = filters.client || (userId ? userId : undefined);
    let queryStr = `?page=${page}&client=${clientParam}&sortField=${sortField ? sortField : undefined}&sortOrder=${
      sortOrder ? sortOrder : undefined
    }`;
    if (dateRange) {
      queryStr = queryStr + `&dateRange=${dateRange}`;
    }
    if (searchStr) {
      queryStr = queryStr + `&search=${searchStr}`;
    }
    if (filters.noteTypeFilter) queryStr += `&noteType=${filters.noteTypeFilter}`;
    if (filters.facility) queryStr += `&facility=${filters.facility}`;
    if (filters.createdBy) queryStr += `&createdBy=${filters.createdBy}`;
    if (filters.approvalStatus) queryStr += `&approvalStatus=${filters.approvalStatus}`;
    if (filters.includeDrafts) queryStr += `&includeDrafts=true`;
    let response = await https().get(`/note${queryStr}`);
    // Discard a stale response: a newer getNotes call has since started, so
    // applying this one would overwrite fresher rows/counts with old data.
    if (reqId !== notesRequestSeq) return;
    if (response?.status === 200) {
      store.dispatch(setNotes(response?.data));
      store.dispatch(setNotePageLoaging(false));
    }
  } catch (error) {
    if (reqId !== notesRequestSeq) return;
    store.dispatch(setNotePageLoaging(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// Same /note list endpoint as getNotes (page 1, sorted by createdAt
// descending = the backend's most-recent-10), but returns the notes
// directly instead of dispatching into the shared `notes` slice — the
// "Use a Previous Note" picker needs its own unfiltered recent list without
// clobbering whatever the main table is currently showing.
const getRecentNotesForPicker = async (clientId) => {
  try {
    // When opened from a client's profile, scope the picker to that client's
    // own previous notes (the /note endpoint filters by fields.Client Name /
    // Patient Name, matching array-valued multi-client notes too).
    let url = `/note?page=1&sortField=createdAt&sortOrder=descending`;
    if (clientId) url += `&client=${clientId}`;
    let response = await https().get(url);
    if (response?.status === 200) {
      return response?.data?.notes || [];
    }
    return [];
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return [];
  }
};

const getSingleNote = async (noteId, userId, showLoading = true) => {
  try {
    if (showLoading) store.dispatch(setLoading(true));
    store.dispatch(setSingleNote(null));
    let response = await https().get(`/note/${noteId}`);

    if (response?.status === 200) {
      store.dispatch(setSingleNote(response?.data?.note));
      return response?.data?.note;
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const getNotesByReport = async (id) => {
  try {
    store.dispatch(setLoadingNote_Types(true));
    let response = await https().post(`/note/getNotesByReport`, {
      reportId: id,
    });

    if (response?.status === 200) {
      store.dispatch(setNotesAgainstType(response?.data));
      store.dispatch(setLoadingNote_Types(false));
    }
  } catch (error) {
    store.dispatch(setLoadingNote_Types(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const getNotesReport = async (payload, showLoading = true) => {
  try {
    if (showLoading) store.dispatch(setLoadingNote_Report(true));
    let response = await https().post("/note/notesReport", payload);

    if (response?.status === 200) {
      store.dispatch(setNote_Report(response?.data?.data));
      store.dispatch(setLoadingNote_Report(false));
      return response?.data?.data;
    }
  } catch (error) {
    store.dispatch(setLoadingNote_Report(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// get Billing Notes

const getBillingNotes = async (dateRange) => {
  try {
    let queryStr = "";

    if (dateRange && dateRange.start_date && dateRange.end_date) {
      queryStr = `?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
    }

    let response = await https().get(`/note/get-billing-notes${queryStr}`);

    if (response?.status === 200) {
      store.dispatch(setBillingNotes(response?.data));
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// POST

const createNoteType = async (data) => {
  try {
    store.dispatch(setAddNoteLoading(true));
    let response = await https().post("/ntype", data);

    if (response?.status === 200) {
      getNoteTypes();
    }
  } catch (error) {
    store.dispatch(setAddNoteLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const createNote = async (
  data,
  callback,
  refreshUserId = data?.userId,
  currentPage = 1,
  dateRange,
  sortField,
  sortOrder,
  activeFilters
) => {
  try {
    store.dispatch(setAddNoteLoading(true));
    let response = await https().post("/note", data);

    if (response?.status === 200) {
      getNotes(currentPage, refreshUserId || null, false, dateRange, sortField, sortOrder, undefined, activeFilters);
      // await swal({
      //   title: `Note Added`,
      //   text: "Note has been added successfully",
      //   icon: "success",
      //   dangerMode: false,
      //   buttons: ["Cancel", "OK"],
      // });
      callback?.();
      return response?.data?.note;
    }
  } catch (error) {
    let _error = error?.response?.data?.message;
    if (_error?.includes("duplicate") && _error?.includes("name"))
      showError("This name is already taken");
    else showError(_error, error?.response?.status);
    store.dispatch(setAddNoteLoading(false));
    // showError(error?.response?.data?.message, error?.response?.status)
    return false;
  }
};

// PUT
const editNoteType = async (data, callback) => {
  try {
    store.dispatch(setLoading(true));

    let response = await https().put("/ntype", data);

    if (response?.status === 200) {
      getNoteTypes(1);

      callback?.();
    }
  } catch (error) {
    let _error = error?.response?.data?.message;

    if (_error?.includes("duplicate") && _error?.includes("name"))
      showError("Note Name already exists");
    else showError(_error, error?.response?.status);
    store.dispatch(setLoading(false));
    return false;
    // showError(error?.response?.data?.message, error?.response?.status)
  }
};

// NOTE APPROVAL
const noteTypeApproval = async (data, callback) => {
  try {
    // store.dispatch(setLoading(true));
    let response = await https().put("/ntype/noteTypesApproval", data);
    if (response?.status === 200) {
      // getNoteTypes(1);
      callback?.();
    }
    return response;
  } catch (error) {
    let _error = error?.response?.data?.message;

    if (_error?.includes("duplicate") && _error?.includes("name"))
      showError("Note Name already exists");
    else showError(_error, error?.response?.status);
    // store.dispatch(setLoading(false));
    return false;
    // showError(error?.response?.data?.message, error?.response?.status)
  }
};

const editNote = async (
  data,
  showLoading = true,
  callback = null,
  sortField,
  sortOrder,
  setEditModal,
  currentPage = 1,
  dateRange,
  activeFilters
) => {
  try {
    store.dispatch(setAddNoteLoading(true));
    store.dispatch(setNotePageLoaging(true));

    let response = await https().put(`/note`, data);

    if (response?.status === 200) {
      data?.userId
        ? getNotes(currentPage, data?.userId, false, dateRange, sortField, sortOrder, undefined, activeFilters)
        : getNotes(currentPage, null, false, dateRange, sortField, sortOrder, undefined, activeFilters);
      data?.userId
        ? getPlans(1, data?.userId, null, false)
        : getPlans(1, null, null, false);

      // getNotes();
      // await swal({
      //   title: `Note Edited`,
      //   text: "Note has been edited successfully",
      //   icon: "success",
      //   dangerMode: false,
      //   buttons: ["Cancel", "OK"],
      // });
      setEditModal(true);
      callback?.();
      return data?.id;
    }
  } catch (error) {
    let _error = error?.response?.data?.message;
    if (_error?.includes("duplicate") && _error?.includes("name"))
      showError("This name is already taken");
    else showError(_error, error?.response?.status);
    store.dispatch(setAddNoteLoading(false));
    store.dispatch(setNotePageLoaging(false));

    // showError(error?.response?.data?.message, error?.response?.status)
    return false;
  }
};

// Note Status Update
const updateNoteStatus = async (data) => {
  try {
    store.dispatch(setAddNoteLoading(true));
    let response = await https().put(`/note/note-status`, data);

    if (response?.status === 200) {
      await swal({
        title: response?.data?.message?.toUpperCase(),
        text: `${response?.data?.message} successfully!`,
        icon: "success",
        dangerMode: false,
        buttons: ["Cancel", "OK"],
      });
      // callback?.();
    }
    return response;
  } catch (error) {
    let _error = error?.response?.data?.message;
    await swal({
      title: `Note Status Update Failed`,
      text: _error,
      icon: "error",
      dangerMode: false,
      buttons: ["Cancel", "OK"],
    });
    store.dispatch(setAddNoteLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

// DELETE
const remoteNoteType = async (id, callback) => {
  try {
    store.dispatch(setAddNoteLoading(true));
    let response = await https().delete(`/ntype?id=${id}`);

    if (response?.status === 200) {
      getNoteTypes(false);
      callback?.();
    }
  } catch (error) {
    store.dispatch(setAddNoteLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const removeNote = async (
  data,
  userId,
  callback,
  sortField,
  sortOrder,
  isDashboradMode,
  currentPage = 1,
  dateRange,
  activeFilters
) => {
  try {
    store.dispatch(setNotePageLoaging(true));
    if (isDashboradMode) {
      store.dispatch(setDashboardNotes(true));
    }
    let response = await https().delete(`/note/${data?.id}`);

    if (response?.status === 200) {
      if (isDashboradMode) {
        getTodaysNotes("", false);
      } else {
        userId
          ? getNotes(currentPage, userId, false, dateRange, sortField, sortOrder, undefined, activeFilters)
          : getNotes(currentPage, null, false, dateRange, sortField, sortOrder, undefined, activeFilters);
      }
      callback?.();
    }
  } catch (error) {
    store.dispatch(setNotePageLoaging(false));
    if (isDashboradMode) {
      store.dispatch(setDashboardNotes(false));
    }
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// generateAiNote / generateBothAiNotes accept either:
//   - new format: { action, context }  — backend assembles the prompt
//   - legacy format: a pre-built prompt string (for any callers not yet migrated)
const generateAiNote = async (actionOrPrompt, profileId, provider = "openai") => {
  try {
    const body =
      typeof actionOrPrompt === "object" && actionOrPrompt !== null
        ? { action: actionOrPrompt.action, context: actionOrPrompt.context, provider }
        : { prompt: actionOrPrompt, provider };
    const response = await https().post("/note/ai-response", body);
    reportSkaiSpend(response?.data?.skaiTokens, "note/ai-response");
    getSkaiTokenUsage(profileId);
    return response.data;
  } catch (error) {
    reportSkaiRejection(error, "note/ai-response");
    throw error;
  }
};

const generateBothAiNotes = async (actionOrPrompt, profileId) => {
  try {
    const body =
      typeof actionOrPrompt === "object" && actionOrPrompt !== null
        ? { action: actionOrPrompt.action, context: actionOrPrompt.context, provider: "both" }
        : { prompt: actionOrPrompt, provider: "both" };
    const response = await https().post("/note/ai-response", body);
    reportSkaiSpend(response?.data?.skaiTokens, "note/ai-response:both");
    getSkaiTokenUsage(profileId);
    return response.data;
  } catch (error) {
    reportSkaiRejection(error, "note/ai-response:both");
    throw error;
  }
};

//   export const getSkaiTokenUsage = async (id, showLoading = true) => {
//   try {
//     if (showLoading) store.dispatch(setLoading(true));

//     const response = await https().get(`/skai/tokens-usage/${id}`);

//     if (response?.status === 200) {
//       store.dispatch(setSkaiTokenUsage(response?.data));
//     }

//   } catch (error) {
//     showError(error?.response?.data?.message, error?.response?.status);
//   } finally {
//     if (showLoading) store.dispatch(setLoading(false));
//   }
// };

const transcribeAudio = async (audioBlob) => {
  const formData = new FormData();
  formData.append("file", audioBlob, "recording.webm");

  try {
    const response = await https().post("/note/voice-transcribe", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data.transcript;
  } catch (error) {
    throw error;
  }
};

// ── Note Drafts (auto-save + drafts list) ──
const saveNoteDraft = async (data) => {
  try {
    const response = await https().put("/note/draft", data);
    if (response?.status === 200) {
      store.dispatch(bumpDraftSaved());
      return true;
    }
    return false;
  } catch {
    return false; // Silent failure — don't interrupt the user
  }
};

const getNoteDraft = async (noteTypeId, clientId, facilityId) => {
  try {
    const url = facilityId ? `/note/draft/${noteTypeId}/${clientId}/${facilityId}` : `/note/draft/${noteTypeId}/${clientId}`;
    const response = await https().get(url);
    return response?.data?.draft || null;
  } catch {
    return null;
  }
};

const deleteNoteDraft = async (noteTypeId, clientId) => {
  try {
    await https().delete(`/note/draft/${noteTypeId}/${clientId}`);
  } catch {
    // Silent failure
  }
};

const logAiSuggestionEvent = async (data) => {
  try {
    await https().post("/analytics/ai-suggestion-event", data);
  } catch {
    // fire-and-forget — never block the user
  }
};

// The backend now creates the AiSuggestionEvent itself at generation time
// (/note/ai-response returns suggestionEventId) — this only reports the
// two fields only the frontend can know: which option the user picked, and
// how many times they regenerated before deciding.
const updateAiSuggestionEvent = async (eventId, data) => {
  if (!eventId) return;
  try {
    await https().patch(`/analytics/ai-suggestion-event/${eventId}`, data);
    } catch {
    // fire-and-forget — never block the user
  }
};
// OD-1164: sends AI original + user-edited saved text to the exemplar store.
// The backend de-identifies (PHI/PII scrub) before anything is persisted.
const captureExemplarNotes = async (data) => {
  try {
    await https().post("/exemplar-notes/capture", data);
  } catch {
    // fire-and-forget — never block the user
  }
};

const getAllDrafts = async (dispatch, clientId = null, showLoading = true) => {
  if (showLoading) dispatch(setDraftsLoading(true));
  try {
    const url = clientId ? `/note/drafts?clientId=${clientId}` : "/note/drafts";
    const res = await https().get(url);
    dispatch(setDrafts(res?.data?.drafts || []));
  } catch (err) {
    showError(err?.response?.data?.message, err?.response?.status);
    if (showLoading) dispatch(setDraftsLoading(false));
  }
};

export {
  createNote,
  createNoteType,
  editNote,
  editNoteType,
  getAllNoteTypes,
  getFieldTypes,
  getNoteTypes,
  getNotes,
  getRecentNotesForPicker,
  getSingleNote,
  getNotesByReport,
  remoteNoteType,
  removeNote,
  noteTypeApproval,
  updateNoteStatus,
  getBillingNotes,
  generateAiNote,
  generateBothAiNotes,
  transcribeAudio,
  getNotesReport,
  saveNoteDraft,
  getNoteDraft,
  deleteNoteDraft,
  getAllDrafts,
  logAiSuggestionEvent,
  updateAiSuggestionEvent,
  captureExemplarNotes,
};
